import React, { useState } from 'react';

interface Performer {
  id: number;
  name: string;
  hourlyRate: number;
  hours: number;
  travel: number;
}

interface AddOn {
  id: number;
  name: string;
  price: number;
  fee: number;
}

interface EventDetails {
  date: string;
  startTime: string;
  endTime: string;
  address: string;
  serviceDescription: string;
  payId: string;
  payIdName: string;
  deposit: number;
}

export interface QuoteData {
  eventDetails: EventDetails;
  performers: Performer[];
  addOns: AddOn[];
  disclaimer: string;
  sameTravel: boolean;
  globalTravel: number;
  sameRate: boolean;
  globalRate: number;
  sameHours: boolean;
  globalHours: number;
}

// Helper for numeric inputs that work well on mobile
const NumericInput: React.FC<{
  value: number;
  onChange: (value: number) => void;
  className?: string;
  disabled?: boolean;
  step?: string;
  placeholder?: string;
}> = ({ value, onChange, className, disabled, step, placeholder }) => {
  const [displayValue, setDisplayValue] = useState(value === 0 ? '' : String(value));

  // Sync display when external value changes
  React.useEffect(() => {
    setDisplayValue(value === 0 ? '' : String(value));
  }, [value]);

  return (
    <input
      type="text"
      inputMode={step === '0.5' ? 'decimal' : 'numeric'}
      pattern={step === '0.5' ? '[0-9]*\\.?[0-9]*' : '[0-9]*'}
      value={displayValue}
      onChange={(e) => {
        const raw = e.target.value;
        // Allow empty, digits, and decimal point for step fields
        if (raw === '' || /^[0-9]*\.?[0-9]*$/.test(raw)) {
          setDisplayValue(raw);
          const num = parseFloat(raw) || 0;
          onChange(num);
        }
      }}
      onBlur={() => {
        // Format on blur - show empty for 0, otherwise show the number
        const num = parseFloat(displayValue) || 0;
        setDisplayValue(num === 0 ? '' : String(num));
      }}
      onFocus={(e) => e.target.select()}
      className={className}
      disabled={disabled}
      placeholder={placeholder || '0'}
    />
  );
};

const QuoteGenerator: React.FC = () => {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  const [eventDetails, setEventDetails] = useState<EventDetails>({
    date: '2025-01-22',
    startTime: '14:30',
    endTime: '18:00',
    address: 'Client Name & Address Here',
    serviceDescription: 'Service description goes here',
    payId: '0412124804',
    payIdName: 'Lauren Malar',
    deposit: 600,
  });

  const [performers, setPerformers] = useState<Performer[]>([
    { id: 1, name: 'Lola', hourlyRate: 110, hours: 4, travel: 100 },
    { id: 2, name: 'Ciara', hourlyRate: 110, hours: 4, travel: 100 },
    { id: 3, name: 'Chanel', hourlyRate: 110, hours: 4, travel: 100 },
  ]);

  const [sameTravel, setSameTravel] = useState(true);
  const [globalTravel, setGlobalTravel] = useState(100);

  const [sameRate, setSameRate] = useState(true);
  const [globalRate, setGlobalRate] = useState(110);

  const [sameHours, setSameHours] = useState(true);
  const [globalHours, setGlobalHours] = useState(4);

  const [addOns, setAddOns] = useState<AddOn[]>([
    { id: 1, name: 'Clothed', price: 110, fee: 40 },
    { id: 2, name: 'Bikini/lingerie', price: 130, fee: 50 },
    { id: 3, name: 'Topless', price: 150, fee: 60 },
  ]);

  const [viewMode, setViewMode] = useState<'client' | 'internal'>('client');

  const [disclaimer, setDisclaimer] = useState(`*Strictly no female guests.* please advise prior to the booking if you anticipate there will be females attending some workers will not take jobs where other women are - Please disregard 
    
    At all times:
•⁠  ⁠No filming or taking photos without consent 
•⁠  ⁠No derogatory or profane comments
•⁠  ⁠No throwing objects
•⁠  ⁠No unauthorised or inappropriate touching
•⁠  ⁠⁠No heavy substance abuse
•⁠  ⁠⁠No changes of start time, entertainers or reducing number of the entertainers confirmed
•⁠  ⁠⁠Please do not ask for the entertainer's phone number or social media 
•⁠  ⁠⁠Deposit is non refundable 

If any of these happen, especially photos/filming, the girls reserve the right to leave NO REFUNDS WILL BE GIVEN`);

  const performerCount = performers.length;

  const getPerformerRate = (performer: Performer) => sameRate ? globalRate : performer.hourlyRate;
  const getPerformerTravel = (performer: Performer) => sameTravel ? globalTravel : performer.travel;
  const getPerformerHours = (performer: Performer) => sameHours ? globalHours : performer.hours;

  const calculatePerformerTotal = (performer: Performer) => {
    const rate = getPerformerRate(performer);
    const travel = getPerformerTravel(performer);
    const hours = getPerformerHours(performer);
    return (hours * rate) + travel;
  };

  const totalServiceAmount = performers.reduce((sum, p) => {
    const rate = getPerformerRate(p);
    const hours = getPerformerHours(p);
    return sum + (hours * rate);
  }, 0);

  const totalTravelAmount = sameTravel
    ? performerCount * globalTravel
    : performers.reduce((sum, p) => sum + p.travel, 0);

  const grandTotal = totalServiceAmount + totalTravelAmount;
  const remainingAfterDeposit = grandTotal - eventDetails.deposit;

  const calculatePerformerCash = (performer: Performer) => {
    if (grandTotal === 0) return 0;
    const performerTotal = calculatePerformerTotal(performer);
    const performerShare = performerTotal / grandTotal;
    return Math.round(remainingAfterDeposit * performerShare);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const formatTime = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    const period = h >= 12 ? 'pm' : 'am';
    const hour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${hour}:${m.toString().padStart(2, '0')}${period}`;
  };

  const addPerformer = () => {
    const newId = Math.max(...performers.map(p => p.id), 0) + 1;
    setPerformers([...performers, { id: newId, name: '', hourlyRate: globalRate, hours: globalHours, travel: globalTravel }]);
  };

  const removePerformer = (id: number) => {
    if (performers.length > 1) {
      setPerformers(performers.filter(p => p.id !== id));
    }
  };

  const updatePerformer = (id: number, field: keyof Performer, value: string | number) => {
    setPerformers(performers.map(p =>
      p.id === id ? { ...p, [field]: (field === 'travel' || field === 'hourlyRate' || field === 'hours') ? Number(value) : value } : p
    ));
  };

  const addAddOn = () => {
    const newId = Math.max(...addOns.map(a => a.id), 0) + 1;
    setAddOns([...addOns, { id: newId, name: '', price: 0, fee: 0 }]);
  };

  const removeAddOn = (id: number) => {
    setAddOns(addOns.filter(a => a.id !== id));
  };

  const updateAddOn = (id: number, field: keyof AddOn, value: string | number) => {
    setAddOns(addOns.map(a =>
      a.id === id ? { ...a, [field]: (field === 'price' || field === 'fee') ? Number(value) : value } : a
    ));
  };

  const generateQuoteText = (mode: 'client' | 'internal' = 'client') => {
    let text = `#CONFIRMATION\n`;
    text += `${formatDate(eventDetails.date)}\n`;
    text += `${formatTime(eventDetails.startTime)}- ${formatTime(eventDetails.endTime)}\n`;
    text += `Address: ${eventDetails.address}\n\n`;
    text += `Performers:\n`;
    performers.forEach(p => {
      text += `${p.name || 'Unnamed'} ${calculatePerformerCash(p)} cash\n`;
    });
    text += `\n• Service ${performerCount} girls`;
    if (sameHours) {
      text += ` ${globalHours} hours`;
    }
    text += ` ${eventDetails.serviceDescription}`;
    if (sameRate) {
      text += ` ${globalRate}ph\n`;
    } else {
      text += `\n`;
    }
    if (!sameHours || !sameRate) {
      performers.forEach(p => {
        const hours = getPerformerHours(p);
        const rate = getPerformerRate(p);
        text += `  ${p.name || 'Unnamed'}: ${hours}h @ ${rate}ph\n`;
      });
    }
    if (sameTravel) {
      text += `Plus individual travel ${globalTravel} each performer\n`;
    } else {
      performers.forEach(p => {
        text += `${p.name || 'Unnamed'} travel: ${p.travel}\n`;
      });
    }
    text += `\n• Payment: Please prepare cash on arrival\n`;
    text += `Transfer to ${eventDetails.payId} pay ID (${eventDetails.payIdName})\n\n`;
    text += `TOTAL:\n${grandTotal}\n`;
    text += `DEPOSIT\n${eventDetails.deposit}\n\n`;
    if (addOns.filter(a => a.name && a.price > 0).length > 0) {
      text += `Any extensions per performer:\n`;
      addOns.filter(a => a.name && a.price > 0).forEach(a => {
        if (mode === 'internal') {
          const performerKeeps = a.price - a.fee;
          text += ` ${a.price} ${a.name} (Performer: ${performerKeeps} | Fee: ${a.fee})\n`;
        } else {
          text += ` ${a.price} ${a.name}\n`;
        }
      });
    }
    text += `\nIMPORTANT TERMS & CONDITIONS\n`;
    text += disclaimer;

    if (mode === 'internal') {
      text += `\n\n--- INTERNAL SUMMARY ---\n`;
      text += `Total Add-on Fees Available: ${addOns.filter(a => a.name && a.price > 0).reduce((sum, a) => sum + a.fee, 0)} per extension\n`;
    }

    return text;
  };

  const copyToClipboard = async (mode: 'client' | 'internal' = 'client') => {
    try {
      await navigator.clipboard.writeText(generateQuoteText(mode));
      setCopySuccess(mode === 'client' ? 'Client quote copied!' : 'Internal quote copied!');
      setTimeout(() => setCopySuccess(null), 3000);
    } catch {
      setCopySuccess('Failed to copy');
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  const openPrintView = (mode: 'client' | 'internal' = 'client') => {
    const quoteData: QuoteData = {
      eventDetails,
      performers,
      addOns,
      disclaimer,
      sameTravel,
      globalTravel,
      sameRate,
      globalRate,
      sameHours,
      globalHours,
    };

    localStorage.setItem('quoteData', JSON.stringify(quoteData));
    localStorage.setItem('quoteMode', mode);

    window.open('/tools/quote-generator/print', '_blank');
  };

  // Input field styles
  const inputClass = "w-full px-3 py-2 rounded-lg bg-[#1e293b] border border-[#334155] text-white placeholder-[#64748b] focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent";
  const labelClass = "block text-sm font-semibold text-[#e2e8f0] mb-1";

  return (
    <div className="min-h-screen bg-[#0a0e27] p-4 md:p-6 overflow-x-hidden">
      {copySuccess && (
        <div className="fixed top-4 right-4 z-50 bg-[#10b981] text-white px-6 py-3 rounded-lg shadow-lg font-medium animate-pulse">
          {copySuccess}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <h1 className="text-center text-white text-2xl md:text-3xl font-bold mb-6">
          Corporate Bookings Quote Generator
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          {/* Input Section */}
          <div className="glass-card rounded-2xl p-4 sm:p-6 shadow-2xl overflow-hidden">
            <h2 className="text-white text-xl font-bold border-b-2 border-[#0ea5e9] pb-2 mb-4">
              Event Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Date</label>
                <input
                  type="date"
                  value={eventDetails.date}
                  onChange={(e) => setEventDetails({...eventDetails, date: e.target.value})}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Address</label>
                <input
                  type="text"
                  value={eventDetails.address}
                  onChange={(e) => setEventDetails({...eventDetails, address: e.target.value})}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Start Time</label>
                <input
                  type="time"
                  value={eventDetails.startTime}
                  onChange={(e) => setEventDetails({...eventDetails, startTime: e.target.value})}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>End Time</label>
                <input
                  type="time"
                  value={eventDetails.endTime}
                  onChange={(e) => setEventDetails({...eventDetails, endTime: e.target.value})}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Deposit ($)</label>
                <NumericInput
                  value={eventDetails.deposit}
                  onChange={(val) => setEventDetails({...eventDetails, deposit: val})}
                  className={inputClass}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className={labelClass}>Service Description</label>
              <input
                type="text"
                value={eventDetails.serviceDescription}
                onChange={(e) => setEventDetails({...eventDetails, serviceDescription: e.target.value})}
                className={inputClass}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelClass}>Pay ID</label>
                <input
                  type="text"
                  value={eventDetails.payId}
                  onChange={(e) => setEventDetails({...eventDetails, payId: e.target.value})}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Pay ID Name</label>
                <input
                  type="text"
                  value={eventDetails.payIdName}
                  onChange={(e) => setEventDetails({...eventDetails, payIdName: e.target.value})}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Performers Section */}
            <h2 className="text-white text-xl font-bold border-b-2 border-[#0ea5e9] pb-2 mb-4 mt-8">
              Performers
            </h2>

            {/* Global Settings */}
            <div className="bg-[#0f172a] rounded-xl p-4 mb-4 border border-[#334155]">
              <p className="text-xs text-[#0ea5e9] font-medium mb-3">Default values for all girls (uncheck to set individually):</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* Hours */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sameHours}
                    onChange={(e) => setSameHours(e.target.checked)}
                    className="w-4 h-4 text-[#0ea5e9] rounded focus:ring-[#0ea5e9] bg-[#1e293b] border-[#334155]"
                  />
                  <label className="text-xs text-[#94a3b8]">Hours:</label>
                  <NumericInput
                    value={globalHours}
                    onChange={(val) => setGlobalHours(val)}
                    disabled={!sameHours}
                    className={`w-16 px-2 py-1 rounded border text-sm ${sameHours ? 'border-[#0ea5e9] bg-[#1e293b] text-white' : 'border-[#334155] bg-[#0f172a] text-[#64748b]'}`}
                    step="0.5"
                  />
                </div>
                {/* Rate */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sameRate}
                    onChange={(e) => setSameRate(e.target.checked)}
                    className="w-4 h-4 text-[#0ea5e9] rounded focus:ring-[#0ea5e9] bg-[#1e293b] border-[#334155]"
                  />
                  <label className="text-xs text-[#94a3b8]">Rate:</label>
                  <div className="flex items-center">
                    <span className="text-[#64748b] text-sm">$</span>
                    <NumericInput
                      value={globalRate}
                      onChange={(val) => setGlobalRate(val)}
                      disabled={!sameRate}
                      className={`w-16 px-2 py-1 rounded border text-sm ${sameRate ? 'border-[#0ea5e9] bg-[#1e293b] text-white' : 'border-[#334155] bg-[#0f172a] text-[#64748b]'}`}
                    />
                    <span className="text-[#64748b] text-xs">/hr</span>
                  </div>
                </div>
                {/* Travel */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sameTravel}
                    onChange={(e) => setSameTravel(e.target.checked)}
                    className="w-4 h-4 text-[#0ea5e9] rounded focus:ring-[#0ea5e9] bg-[#1e293b] border-[#334155]"
                  />
                  <label className="text-xs text-[#94a3b8]">Travel:</label>
                  <div className="flex items-center">
                    <span className="text-[#64748b] text-sm">$</span>
                    <NumericInput
                      value={globalTravel}
                      onChange={(val) => setGlobalTravel(val)}
                      disabled={!sameTravel}
                      className={`w-16 px-2 py-1 rounded border text-sm ${sameTravel ? 'border-[#0ea5e9] bg-[#1e293b] text-white' : 'border-[#334155] bg-[#0f172a] text-[#64748b]'}`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Performer Cards */}
            {performers.map((performer) => (
              <div key={performer.id} className="mb-3 p-3 bg-[#0f172a] rounded-lg border border-[#334155]">
                <div className="flex gap-2 items-center mb-2">
                  <input
                    type="text"
                    value={performer.name}
                    onChange={(e) => updatePerformer(performer.id, 'name', e.target.value)}
                    placeholder="Girl name"
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-[#1e293b] border border-[#334155] text-white placeholder-[#64748b] focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent text-sm font-medium"
                  />
                  <button
                    onClick={() => removePerformer(performer.id)}
                    disabled={performers.length === 1}
                    className={`shrink-0 px-3 py-2 rounded-lg font-bold text-white transition-colors ${
                      performers.length === 1
                        ? 'bg-[#334155] cursor-not-allowed'
                        : 'bg-[#f43f5e] hover:bg-[#e11d48]'
                    }`}
                  >
                    X
                  </button>
                </div>
                {/* Pricing row */}
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-[#64748b]">Hours:</label>
                    {sameHours ? (
                      <span className="text-sm font-medium text-[#0ea5e9] bg-[#0ea5e9]/20 px-2 py-0.5 rounded">{globalHours}</span>
                    ) : (
                      <NumericInput
                        value={performer.hours}
                        onChange={(val) => updatePerformer(performer.id, 'hours', val)}
                        step="0.5"
                        className="w-14 px-2 py-1 rounded border border-[#334155] bg-[#1e293b] text-white focus:ring-2 focus:ring-[#0ea5e9] text-sm"
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-[#64748b]">Rate:</label>
                    {sameRate ? (
                      <span className="text-sm font-medium text-[#0ea5e9] bg-[#0ea5e9]/20 px-2 py-0.5 rounded">${globalRate}/hr</span>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-[#64748b] text-sm">$</span>
                        <NumericInput
                          value={performer.hourlyRate}
                          onChange={(val) => updatePerformer(performer.id, 'hourlyRate', val)}
                          className="w-14 px-2 py-1 rounded border border-[#334155] bg-[#1e293b] text-white focus:ring-2 focus:ring-[#0ea5e9] text-sm"
                        />
                        <span className="text-[#64748b] text-xs">/hr</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-[#64748b]">Travel:</label>
                    {sameTravel ? (
                      <span className="text-sm font-medium text-[#0ea5e9] bg-[#0ea5e9]/20 px-2 py-0.5 rounded">${globalTravel}</span>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-[#64748b] text-sm">$</span>
                        <NumericInput
                          value={performer.travel}
                          onChange={(val) => updatePerformer(performer.id, 'travel', val)}
                          className="w-14 px-2 py-1 rounded border border-[#334155] bg-[#1e293b] text-white focus:ring-2 focus:ring-[#0ea5e9] text-sm"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 bg-[#10b981]/20 px-2 py-1 rounded-lg ml-auto">
                    <span className="text-xs text-[#10b981]">Total:</span>
                    <span className="text-sm text-[#10b981] font-bold">${calculatePerformerTotal(performer)}</span>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addPerformer}
              className="w-full py-3 rounded-lg border-2 border-dashed border-[#0ea5e9] text-[#0ea5e9] font-semibold hover:bg-[#0ea5e9]/10 transition-colors mt-2"
            >
              + Add Performer
            </button>

            {/* Add-ons Section */}
            <h2 className="text-white text-xl font-bold border-b-2 border-[#0ea5e9] pb-2 mb-4 mt-8">
              Extension Add-ons
            </h2>

            <p className="text-xs text-[#94a3b8] mb-3">
              Set the client price and your company fee. The performer keeps the difference.
            </p>

            {addOns.map((addOn) => (
              <div key={addOn.id} className="mb-3 p-3 bg-[#0f172a] rounded-lg border border-[#334155]">
                <div className="flex gap-2 items-center mb-2">
                  <input
                    type="text"
                    value={addOn.name}
                    onChange={(e) => updateAddOn(addOn.id, 'name', e.target.value)}
                    placeholder="Add-on name (e.g. Fire breathing)"
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-[#1e293b] border border-[#334155] text-white placeholder-[#64748b] focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent text-sm"
                  />
                  <button
                    onClick={() => removeAddOn(addOn.id)}
                    className="shrink-0 px-3 py-2 rounded-lg bg-[#f43f5e] hover:bg-[#e11d48] text-white font-bold transition-colors"
                  >
                    X
                  </button>
                </div>
                {/* Price inputs */}
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-[#64748b] whitespace-nowrap">Client pays:</label>
                    <div className="flex items-center">
                      <span className="text-[#64748b] text-sm">$</span>
                      <NumericInput
                        value={addOn.price}
                        onChange={(val) => updateAddOn(addOn.id, 'price', val)}
                        placeholder="0"
                        className="w-16 px-2 py-1.5 rounded-lg border border-[#334155] bg-[#1e293b] text-white focus:ring-2 focus:ring-[#0ea5e9] text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-[#64748b] whitespace-nowrap">Company fee:</label>
                    <div className="flex items-center">
                      <span className="text-[#64748b] text-sm">$</span>
                      <NumericInput
                        value={addOn.fee}
                        onChange={(val) => updateAddOn(addOn.id, 'fee', val)}
                        placeholder="0"
                        className="w-16 px-2 py-1.5 rounded-lg border border-[#334155] bg-[#1e293b] text-white focus:ring-2 focus:ring-[#0ea5e9] text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-1 bg-[#10b981]/20 px-2 py-1 rounded-lg">
                    <span className="text-xs text-[#10b981] whitespace-nowrap">Performer keeps:</span>
                    <span className="text-sm text-[#10b981] font-bold">${addOn.price - addOn.fee}</span>
                  </div>
                </div>
              </div>
            ))}

            <button
              onClick={addAddOn}
              className="w-full py-3 rounded-lg border-2 border-dashed border-[#0ea5e9] text-[#0ea5e9] font-semibold hover:bg-[#0ea5e9]/10 transition-colors mt-2"
            >
              + Add Extension Option
            </button>

            {/* Disclaimer Section */}
            <h2 className="text-white text-xl font-bold border-b-2 border-[#0ea5e9] pb-2 mb-4 mt-8">
              Disclaimer
            </h2>
            <textarea
              value={disclaimer}
              onChange={(e) => setDisclaimer(e.target.value)}
              className="w-full px-3 py-3 rounded-lg bg-[#1e293b] border border-[#334155] text-white placeholder-[#64748b] focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent min-h-[150px] resize-y"
            />
          </div>

          {/* Preview Section */}
          <div className="glass-card rounded-2xl p-4 sm:p-6 shadow-2xl md:sticky md:top-6 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b-2 border-[#10b981] pb-2 mb-4">
              <h2 className="text-white text-xl font-bold">
                Quote Preview
              </h2>
              {/* View Mode Toggle */}
              <div className="flex bg-[#0f172a] rounded-lg p-1 text-sm">
                <button
                  onClick={() => setViewMode('client')}
                  className={`px-3 py-1 rounded-md font-medium transition-colors ${
                    viewMode === 'client'
                      ? 'bg-[#1e293b] text-[#0ea5e9] shadow-sm'
                      : 'text-[#64748b] hover:text-[#94a3b8]'
                  }`}
                >
                  Client
                </button>
                <button
                  onClick={() => setViewMode('internal')}
                  className={`px-3 py-1 rounded-md font-medium transition-colors ${
                    viewMode === 'internal'
                      ? 'bg-[#1e293b] text-[#f43f5e] shadow-sm'
                      : 'text-[#64748b] hover:text-[#94a3b8]'
                  }`}
                >
                  Internal
                </button>
              </div>
            </div>

            {viewMode === 'internal' && (
              <div className="bg-[#f43f5e]/20 border border-[#f43f5e]/50 rounded-lg p-2 mb-4 text-xs text-[#f43f5e] font-medium">
                INTERNAL VIEW - Shows company fees (not for clients)
              </div>
            )}

            {/* Calculations Summary */}
            <div className="bg-gradient-to-br from-[#0ea5e9] to-[#06b6d4] text-white p-3 sm:p-4 rounded-xl mb-5">
              <h3 className="font-bold mb-3 text-sm sm:text-base">Calculations</h3>
              <div className="grid grid-cols-2 gap-1 sm:gap-2 text-xs sm:text-sm">
                <div>Girls: <span className="font-bold">{performerCount}</span></div>
                <div>Service Total: <span className="font-bold">${totalServiceAmount}</span></div>
                <div>Travel Total: <span className="font-bold">${totalTravelAmount}</span></div>
                <div className="col-span-2 border-t border-white/30 pt-2 mt-1">
                  <span className="font-bold text-lg">Grand Total: ${grandTotal}</span>
                </div>
                <div>Deposit: <span className="font-bold">${eventDetails.deposit}</span></div>
                <div>Remaining: <span className="font-bold">${remainingAfterDeposit}</span></div>
              </div>

              {/* Per Performer Breakdown */}
              <div className="mt-3 pt-3 border-t border-white/30">
                <span className="font-bold">Cash per performer:</span>
                {performers.map(p => (
                  <div key={p.id} className="text-xs mt-1 flex flex-wrap gap-x-2">
                    <span>{p.name || 'Unnamed'}: <strong>${calculatePerformerCash(p)}</strong></span>
                    <span className="opacity-70">
                      ({getPerformerHours(p)}h x ${getPerformerRate(p)} + ${getPerformerTravel(p)} travel)
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quote Preview */}
            <div className="bg-[#0f172a] p-4 sm:p-5 rounded-xl text-sm leading-relaxed break-words border border-[#334155]">
              <div className="text-xl font-bold mb-4 text-white">#CONFIRMATION</div>

              <div className="mb-4 text-[#e2e8f0]">
                <div>{formatDate(eventDetails.date)}</div>
                <div>{formatTime(eventDetails.startTime)} - {formatTime(eventDetails.endTime)}</div>
                <div>Address: {eventDetails.address}</div>
              </div>

              <div className="mb-4 text-[#e2e8f0]">
                <strong className="text-white">Performers:</strong>
                <div className="ml-1">
                  {performers.map(p => (
                    <div key={p.id}>{p.name || 'Unnamed'} ${calculatePerformerCash(p)} cash</div>
                  ))}
                </div>
              </div>

              <div className="mb-4 text-[#e2e8f0]">
                <div>
                  - Service {performerCount} girls
                  {sameHours && ` ${globalHours} hours`} {eventDetails.serviceDescription}
                  {sameRate && sameHours && ` ${globalRate}ph`}
                </div>
                {(!sameHours || !sameRate) && (
                  <div className="ml-4 text-[#94a3b8]">
                    {performers.map(p => (
                      <div key={p.id}>
                        {p.name || 'Unnamed'}: {getPerformerHours(p)}h @ ${getPerformerRate(p)}ph
                      </div>
                    ))}
                  </div>
                )}
                {sameTravel ? (
                  <div>Plus individual travel ${globalTravel} each performer</div>
                ) : (
                  performers.map(p => (
                    <div key={p.id}>{p.name || 'Unnamed'} travel: ${p.travel}</div>
                  ))
                )}
              </div>

              <div className="mb-4 text-[#e2e8f0]">
                <div>- Payment: Please prepare cash on arrival</div>
                <div>Transfer to {eventDetails.payId} pay ID ({eventDetails.payIdName})</div>
              </div>

              <div className="bg-[#10b981]/20 p-4 rounded-lg mb-4 border border-[#10b981]/30">
                <div className="text-lg font-bold text-white">TOTAL:</div>
                <div className="text-2xl font-bold text-[#10b981]">${grandTotal}</div>
                <div className="mt-2 font-bold text-white">DEPOSIT:</div>
                <div className="text-xl font-bold text-[#0ea5e9]">${eventDetails.deposit}</div>
              </div>

              {addOns.filter(a => a.name && a.price > 0).length > 0 && (
                <div className="mb-4 text-[#e2e8f0]">
                  <strong className="text-white">Any extensions per performer:</strong>
                  {addOns.filter(a => a.name && a.price > 0).map(a => (
                    <div key={a.id}>
                      ${a.price} {a.name}
                      {viewMode === 'internal' && (
                        <span className="text-[#64748b] text-xs ml-2">
                          (Performer: ${a.price - a.fee} | Fee: ${a.fee})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {viewMode === 'internal' && addOns.filter(a => a.name && a.price > 0).length > 0 && (
                <div className="bg-[#3b82f6]/20 p-3 rounded-lg mb-4 border-l-4 border-[#3b82f6]">
                  <div className="font-bold text-[#3b82f6] text-sm mb-1">Internal Summary</div>
                  <div className="text-[#60a5fa] text-xs">
                    Total potential add-on fees: ${addOns.filter(a => a.name && a.price > 0).reduce((sum, a) => sum + a.fee, 0)} per extension booked
                  </div>
                </div>
              )}

              <div className="bg-[#f59e0b]/20 p-4 rounded-lg border-l-4 border-[#f59e0b]">
                <div className="font-bold text-[#f59e0b] mb-2">
                  IMPORTANT TERMS & CONDITIONS
                </div>
                <div className="whitespace-pre-wrap text-[#fcd34d] text-xs">
                  {disclaimer}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-5 space-y-3">
              <div className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">For Clients</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => copyToClipboard('client')}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] text-white font-semibold shadow-md hover:shadow-lg transition-all text-sm hover:opacity-90"
                >
                  Copy Client Quote
                </button>
                <button
                  onClick={() => openPrintView('client')}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#10b981] to-[#14b8a6] text-white font-semibold shadow-md hover:shadow-lg transition-all text-sm hover:opacity-90"
                >
                  Export Client PDF
                </button>
              </div>

              <div className="text-xs font-semibold text-[#64748b] uppercase tracking-wide pt-2">For Internal Use</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => copyToClipboard('internal')}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#f43f5e] to-[#ec4899] text-white font-semibold shadow-md hover:shadow-lg transition-all text-sm hover:opacity-90"
                >
                  Copy Internal Quote
                </button>
                <button
                  onClick={() => openPrintView('internal')}
                  className="flex-1 py-2.5 px-4 rounded-lg bg-gradient-to-r from-[#f59e0b] to-[#f43f5e] text-white font-semibold shadow-md hover:shadow-lg transition-all text-sm hover:opacity-90"
                >
                  Export Internal PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteGenerator;
